import { useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Dialog } from "@/components/common/Dialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { FilterChip } from "@/components/design/FilterChip";
import { Avatar } from "@/components/common/Avatar";
import { Select } from "@/components/design/Select";
import { ShareIcon } from "@/components/share/ShareItemCard";
import { SORT_OPTIONS, type SortDir } from "@/lib/collection-filter";
import { hasDuplicateName } from "@/lib/duplicate-name";

export type GroupSelection = string;

export interface SaveGroupLite {
  id: string;
  name: string;
  itemCount: number;
}

export function SaveCollectionPage({
  title,
  description,
  dataGraphql,
  showBackButton = false,
  sidebar,
  sidebarControls,
  children,
}: {
  title: string;
  description: ReactNode;
  dataGraphql?: string;
  showBackButton?: boolean;
  sidebar: ReactNode;
  sidebarControls?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mt-8 max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10" data-graphql={dataGraphql}>
      {showBackButton ? (
        <div className="mb-5">
          <BackButton />
        </div>
      ) : null}
      <BoxHeader title={title} variant="hero" />
      <div className="smycles-field-meta leading-snug text-ink">{description}</div>

      <div className="mt-5 flex flex-col gap-8 @lg:flex-row">
        {sidebarControls ? (
          <aside className="w-full min-w-0 @lg:w-max @lg:min-w-60 @lg:shrink-0">
            <div>{sidebarControls}</div>
            <div className="mt-4">{sidebar}</div>
          </aside>
        ) : sidebar}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}

export function SaveCollectionControls({
  statusLabel,
  loading,
  errorMessage,
  search,
  sort,
  ungroupedLabel,
  ungroupedActive,
  showUngroupedToggle,
  onSearchChange,
  onSortChange,
  onUngroupedToggle,
}: {
  statusLabel: string;
  loading: boolean;
  errorMessage?: string;
  search: string;
  /** Absent for a list whose order is not the user's to choose (the company search ranks by relevance). */
  sort?: SortDir;
  ungroupedLabel: string;
  ungroupedActive: boolean;
  showUngroupedToggle: boolean;
  onSearchChange: (next: string) => void;
  onSortChange?: (next: SortDir) => void;
  onUngroupedToggle: () => void;
}) {
  const intl = useIntl();
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="min-w-[7rem] text-smycles-meta text-ink-muted">
        {loading ? (
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        ) : errorMessage ? (
          <FormattedMessage
            id="save.collection.errorPrefix"
            defaultMessage="Error: {message}"
            values={{ message: errorMessage }}
          />
        ) : (
          // Eigene Kennung: „geladen" heißt genau dieser Zweig — Ladetext und
          // Fehlertext stehen im selben Kasten und dürfen nicht dafür durchgehen.
          <span id="save-statuscount">{statusLabel}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          id="save-search"
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={intl.formatMessage({ id: "save.collection.searchPlaceholder", defaultMessage: "Search…" })}
          className="w-40 rounded-full bg-page px-3 py-1.5 text-smycles-meta text-ink ring-1 ring-ink/10 transition placeholder:text-ink-muted focus:outline-none focus:ring-ink-ui [&::-webkit-search-cancel-button]:appearance-none"
        />
        {sort && onSortChange ? (
          <Select
            id="save-sort"
            value={sort}
            onChange={(v) => onSortChange(v as SortDir)}
            options={SORT_OPTIONS.map((o) => ({ value: o.value, label: intl.formatMessage({ id: o.labelId }) }))}
            className="w-32 !rounded-full !bg-page !py-1.5 !text-smycles-meta"
          />
        ) : null}
      </div>
      {showUngroupedToggle ? (
        <div className="flex items-center gap-2">
          <FilterChip
            id="save-filter-all"
            active={!ungroupedActive}
            onClick={() => {
              if (ungroupedActive) onUngroupedToggle();
            }}
          >
            <FormattedMessage id="save.collection.allLabel" defaultMessage="Alle" />
          </FilterChip>
          <FilterChip
            id="save-filter-ungrouped"
            active={ungroupedActive}
            onClick={() => {
              if (!ungroupedActive) onUngroupedToggle();
            }}
          >
            {ungroupedLabel}
          </FilterChip>
        </div>
      ) : null}
    </div>
  );
}

export function SaveCollectionEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md bg-ink/[0.02] px-4 py-8 text-center text-smycles-meta text-ink-muted ring-1 ring-ink/15">
      {children}
    </div>
  );
}

export function SaveCollectionGrid({
  variant = "person",
  children,
}: {
  variant?: "person" | "shared";
  children: ReactNode;
}) {
  return (
    <div
      className={`grid grid-cols-1 gap-3 @sm:grid-cols-2 ${
        variant === "person" ? "@xl:grid-cols-3" : "@lg:grid-cols-3 @xl:grid-cols-4"
      }`}
    >
      {children}
    </div>
  );
}

export function SaveCollectionSidebar({
  topControls,
  embedded = false,
  groups,
  activeGroup,
  allLabel,
  allCount,
  addLabel,
  renameLabel,
  deleteLabel,
  onGroupChange,
  onCreateGroup,
  onRenameGroup,
  onDeleteGroup,
  dropTargetId,
  dropHandlers,
}: {
  topControls?: ReactNode;
  embedded?: boolean;
  groups: SaveGroupLite[];
  activeGroup: GroupSelection;
  allLabel?: string;
  allCount?: number;
  addLabel?: string;
  renameLabel?: string;
  deleteLabel?: string;
  onGroupChange: (g: GroupSelection) => void;
  onCreateGroup?: () => void;
  onRenameGroup?: (group: SaveGroupLite) => void;
  onDeleteGroup?: (group: SaveGroupLite) => void;
  dropTargetId?: string | null;
  dropHandlers?: (groupId: string) => {
    onDragOver: (e: DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: DragEvent) => void;
  };
}) {
  const intl = useIntl();
  const resolvedAllLabel =
    allLabel ?? intl.formatMessage({ id: "save.collection.allGroups", defaultMessage: "All" });
  const resolvedRenameLabel =
    renameLabel ??
    intl.formatMessage({ id: "save.collection.renameGroup", defaultMessage: "Rename group" });
  const resolvedDeleteLabel =
    deleteLabel ??
    intl.formatMessage({ id: "save.collection.deleteGroup", defaultMessage: "Delete group" });
  const Container = embedded ? "div" : "aside";
  return (
    <Container className={embedded ? "w-full" : "w-full @lg:w-60 @lg:shrink-0"}>
      {topControls ? <div>{topControls}</div> : null}

      <nav className={topControls ? "mt-4 space-y-0.5" : "space-y-0.5"}>
        <SaveGroupRow
          rowId="save-grouprow-all"
          label={resolvedAllLabel}
          count={allCount}
          active={activeGroup === "all"}
          isDropTarget={dropTargetId === "all"}
          onClick={() => onGroupChange("all")}
          {...dropHandlers?.("all")}
        />

        {groups.length > 0 ? <div className="my-2 border-t border-ink/10" /> : null}

        {groups.map((g) => (
          <SaveGroupRow
            key={g.id}
            rowId={`save-grouprow-${g.id}`}
            label={g.name}
            count={g.itemCount}
            active={activeGroup === g.id}
            isDropTarget={dropTargetId === g.id}
            onClick={() => onGroupChange(g.id)}
            onRename={onRenameGroup ? () => onRenameGroup(g) : undefined}
            onDelete={onDeleteGroup ? () => onDeleteGroup(g) : undefined}
            renameLabel={resolvedRenameLabel}
            deleteLabel={resolvedDeleteLabel}
            {...dropHandlers?.(g.id)}
          />
        ))}
      </nav>

      {onCreateGroup ? <button
        id="save-group-add"
        type="button"
        onClick={onCreateGroup}
        className="mt-4 w-full rounded-md px-3 py-2 text-left text-smycles-meta text-ink ring-1 ring-ink/15 transition hover:bg-ink/[0.02] hover:text-ink"
      >
        {addLabel}
      </button> : null}
    </Container>
  );
}

function SaveGroupRow({
  rowId,
  label,
  count,
  active,
  isDropTarget,
  onClick,
  onRename,
  onDelete,
  renameLabel,
  deleteLabel,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  rowId: string;
  label: string;
  count?: number;
  active: boolean;
  isDropTarget?: boolean;
  onClick: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  renameLabel?: string;
  deleteLabel?: string;
  onDragOver?: (e: DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: DragEvent) => void;
}) {
  const intl = useIntl();
  return (
    <div
      id={rowId}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`group flex items-center gap-1 rounded-md pr-1 transition ${
        isDropTarget
          ? "smycles-drop-target ring-2 ring-accent-decoration"
          : active
            ? "bg-ink/[0.06]"
            : "hover:bg-ink/[0.06]"
      }`}
    >
      <button
        id={`${rowId}-select`}
        type="button"
        onClick={onClick}
        className={`flex flex-1 items-center justify-between gap-2 px-3 py-1.5 text-left text-smycles-meta ${
          active ? "font-medium text-ink" : "text-ink"
        }`}
      >
        <span id={`${rowId}-name`} className="truncate">
          {label}
        </span>
        {typeof count === "number" ? (
          <span className="shrink-0 text-smycles-label tabular-nums text-ink-muted">{count}</span>
        ) : null}
      </button>
      {onRename || onDelete ? (
        <span className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100">
          {onRename ? (
            <button
              id={`${rowId}-rename`}
              type="button"
              onClick={onRename}
              title={intl.formatMessage({ id: "save.collection.rename", defaultMessage: "Rename" })}
              aria-label={
                renameLabel ??
                intl.formatMessage({ id: "save.collection.renameGroup", defaultMessage: "Rename group" })
              }
              className="rounded p-1 text-ink-muted hover:text-ink"
            >
              <ShareIcon name="pencil" className="h-3.5 w-3.5" />
            </button>
          ) : null}
          {onDelete ? (
            <button
              id={`${rowId}-delete`}
              type="button"
              onClick={onDelete}
              title={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
              aria-label={
                deleteLabel ??
                intl.formatMessage({ id: "save.collection.deleteGroup", defaultMessage: "Delete group" })
              }
              className="rounded p-1 text-ink-muted hover:text-ink-accent"
            >
              <ShareIcon name="trash" className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

export function SaveGroupDeleteConfirm({
  open,
  title,
  message,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  return (
    <ConfirmDialog
      id="save-groupdelete"
      open={open}
      title={title}
      message={message}
      confirmLabel={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
      cancelLabel={intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

export function SaveGroupNameDialog({
  open,
  title,
  initial,
  placeholder,
  saving,
  issue,
  submitLabel,
  existingNames,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  initial: string;
  placeholder: string;
  saving?: boolean;
  issue?: string | null;
  submitLabel?: string;
  /**
   * Sibling names this dialog must not collide with. genesis accepts a duplicate pin-group or
   * community name, so two identically named groups could be created and then never told apart.
   * `initial` is excluded automatically, so reopening a rename dialog and pressing Save is fine.
   */
  existingNames?: readonly string[];
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const intl = useIntl();
  const [name, setName] = useState(initial);

  // Dialog keeps its children mounted across open/close, so reset the field to
  // `initial` each time it (re)opens by tracking the prior open/initial pair.
  const [seed, setSeed] = useState({ open, initial });
  if (seed.open !== open || seed.initial !== initial) {
    setSeed({ open, initial });
    if (open) setName(initial);
  }

  const duplicate = hasDuplicateName(existingNames ?? [], name, initial);

  const commit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const name = String(new FormData(e.currentTarget).get("name") ?? "").trim();
    // Re-checked here, not just on the disabled button: Enter submits the form directly.
    if (name && !saving && !hasDuplicateName(existingNames ?? [], name, initial)) onSave(name);
  };

  return (
    <Dialog open={open} onClose={onClose} dismiss="explicit" dirty={name !== initial} preventClose={saving} title={title}>
      <form onSubmit={commit} className="space-y-5">
        <div>
          <div className="smycles-field-label mb-1 text-ink-muted">
            <FormattedMessage id="save.collection.nameLabel" defaultMessage="Name" />
          </div>
          <input
            autoFocus
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            id="save-group-name-input"
            placeholder={placeholder}
            data-testid="save-group-name-input"
            className="smycles-field-value w-full rounded bg-surface px-2 py-1 text-ink ring-1 ring-ink/10 placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-ink-ui"
          />
        </div>

        {/* A name the user has not saved yet is not an error state, so this stays neutral
            smycles-field-meta text rather than the accent used for a rejection from genesis. */}
        {duplicate ? (
          <p
            id="save-group-name-duplicate"
            data-testid="save-group-name-duplicate"
            className="smycles-field-meta text-ink-muted"
          >
            {/* Reuses the key cv-actions already routes genesis' unique-violation to, so the
                pre-check and the server rejection read identically. */}
            <FormattedMessage
              id="common.duplicateName"
              defaultMessage="An entry with this name already exists. Please choose a different name."
            />
          </p>
        ) : null}

        {issue ? (
          <p id="save-group-name-issue" data-testid="save-group-name-issue" className="text-smycles-body text-ink-accent">
            {issue}
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            id="save-group-name-save"
            type="submit"
            variant="primary"
            disabled={saving || !name.trim() || duplicate}
            data-testid="save-group-name-save"
            className={`inline-flex items-center gap-1.5 px-5 py-2 ${
              saving || !name.trim() || duplicate ? "cursor-not-allowed opacity-50" : ""
            }`}
          >
            {saving ? <SaveInlineSpinner /> : null}
            {saving ? (
              <FormattedMessage id="common.saving" defaultMessage="Saving…" />
            ) : (
              submitLabel ?? intl.formatMessage({ id: "common.save", defaultMessage: "Save" })
            )}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function SaveGroupPopover({
  idPrefix,
  triggerLabel,
  options,
  memberIds,
  pendingId,
  glass = false,
  onToggle,
}: {
  // Jede Karte trägt ihr eigenes Popover — die Kennung muss von außen kommen.
  idPrefix?: string;
  triggerLabel: string;
  options: SaveGroupLite[];
  memberIds: Set<string>;
  pendingId: string | null;
  glass?: boolean;
  onToggle: (groupId: string, isMember: boolean) => void;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  if (options.length === 0) return null;
  return (
    <div className="relative">
      <button
        id={idPrefix && `${idPrefix}-trigger`}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center rounded-full px-3 py-1 text-smycles-label text-ink transition ${
          glass
            ? "bg-white/30 shadow-sm ring-1 ring-white/30 backdrop-blur-md hover:bg-surface hover:text-ink"
            : "bg-ink/[0.06] hover:bg-ink/10 hover:text-ink"
        }`}
      >
        {triggerLabel}
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-[var(--z-dropdown)]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-[var(--z-dropdown)] mt-1 flex w-56 max-w-[240px] flex-wrap gap-1 rounded-md bg-surface p-2 shadow-lg ring-1 ring-ink/10">
            {options.map((g) => {
              const isMember = memberIds.has(g.id);
              const pending = pendingId === g.id;
              return (
                <button
                  key={g.id}
                  id={idPrefix && `${idPrefix}-option-${g.id}`}
                  type="button"
                  disabled={pendingId !== null}
                  onClick={() => onToggle(g.id, isMember)}
                  title={
                    isMember
                      ? intl.formatMessage(
                          {
                            id: "save.collection.removeFromGroup",
                            defaultMessage: "Remove from „{name}”",
                          },
                          { name: g.name },
                        )
                      : intl.formatMessage(
                          {
                            id: "save.collection.addToGroup",
                            defaultMessage: "Add to „{name}”",
                          },
                          { name: g.name },
                        )
                  }
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-smycles-label transition disabled:opacity-60 ${
                    isMember
                      ? "bg-accent/15 text-ink-accent hover:bg-red-100 hover:text-red-600"
                      : "bg-ink/[0.06] text-ink hover:bg-accent/15 hover:text-ink-accent"
                  }`}
                >
                  {pending ? <SaveInlineSpinner /> : isMember ? "✓ " : null}
                  {g.name}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function SaveRemoveItemDialog({
  open,
  title,
  message,
  secondaryActionLabel,
  destructiveActionLabel,
  onSecondaryAction,
  onDestructiveAction,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  secondaryActionLabel?: string;
  destructiveActionLabel: string;
  onSecondaryAction?: () => void;
  onDestructiveAction: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} dismiss="explicit" title={title} maxWidth="md">
      <div className="mb-6 text-smycles-body leading-relaxed text-ink">{message}</div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="secondary"
          onClick={onClose}
          className="px-4 py-1.5 text-smycles-meta"
        >
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        {secondaryActionLabel && onSecondaryAction ? (
          <Button
            id="remove-item-secondary"
            variant="secondary"
            onClick={onSecondaryAction}
            className="px-4 py-1.5 text-smycles-meta"
          >
            {secondaryActionLabel}
          </Button>
        ) : null}
        <Button
          id="remove-item-delete"
          variant="primary"
          onClick={onDestructiveAction}
          className="px-4 py-1.5 text-smycles-meta"
        >
          {destructiveActionLabel}
        </Button>
      </div>
    </Dialog>
  );
}

export function SavePersonCollectionCard({
  id,
  title,
  subtitle,
  avatarContent,
  avatarMime,
  draggable,
  onDragStart,
  onDragEnd,
  openHref,
  onRemove,
  removeLabel,
  actions,
}: {
  id: string;
  title: string;
  subtitle?: string;
  avatarContent?: string | null;
  avatarMime?: string | null;
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  openHref?: string;
  onRemove?: () => void;
  removeLabel?: string;
  actions?: ReactNode;
}) {
  const intl = useIntl();

  return (
    <div
      id={id}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`box relative flex items-center gap-3 bg-ink/[0.06] p-3 transition hover:ring-accent-decoration/40 ${
        openHref ? "cursor-pointer" : ""
      } ${draggable ? "active:cursor-grabbing" : ""}`}
    >
      {openHref ? (
        // Stretched link: a real anchor covering the whole card so middle-click,
        // Cmd/Ctrl-click and "open in new tab" work natively, while a plain
        // left-click still routes through the SPA router. draggable={false} lets
        // the drag gesture fall through to the draggable card container.
        <Link
          id={`${id}-open`}
          to={openHref}
          draggable={false}
          aria-label={intl.formatMessage(
            { id: "save.collection.openItem", defaultMessage: "Open {title}" },
            { title },
          )}
          title={intl.formatMessage(
            { id: "save.collection.openItem", defaultMessage: "Open {title}" },
            { title },
          )}
          className="absolute inset-0 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
        />
      ) : null}
      <Avatar person={{ name: title, avatarContent, avatarMime }} size="lg" />
      <div className="min-w-0 flex-1">
        <div id={`${id}-name`} className="truncate text-smycles-meta font-medium leading-snug">
          {title}
        </div>
        {subtitle ? <div className="truncate text-smycles-label text-ink-muted">{subtitle}</div> : null}
      </div>

      {actions || onRemove ? <div className="relative flex shrink-0 items-center gap-1.5">
        {actions}
        {onRemove ? <button
          id={`${id}-remove`}
          type="button"
          onClick={onRemove}
          title={removeLabel}
          aria-label={removeLabel}
          className="rounded-full bg-ink/[0.06] p-1.5 text-ink-muted transition hover:bg-ink/10 hover:text-ink-accent"
        >
          <ShareIcon name="trash" className="h-4 w-4" />
        </button> : null}
      </div> : null}
    </div>
  );
}

export function SaveInlineSpinner({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <span
      className={`inline-block shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
      aria-hidden
    />
  );
}
